import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "@/redux/axios";

// Profile Types
export interface UserProfile {
  user_id?: string;
  email?: string;
  full_name?: string | null;
  phonenumber?: string | null;
  avatar?: string | null;
  bio?: string | null;
  target_exam_year?: string | number | null;
  created_at?: string;
  updated_at?: string;
  referral_code?: string | null;
  total_evaluations?: number;
  total_credits?: number;
  preferred_subjects?: string[];
  weekly_progress_reports?: boolean;
  new_feature_announcements?: boolean;
  member_since?: string;
  wallet?: number;
  notifications?: {
    email_notifications?: boolean;
    push_notifications?: boolean;
    evaluation_updates?: boolean;
    newsletter?: boolean;
  };
}

export interface ProfileUpdateData {
  full_name?: string;
  avatar?: string;
  target_exam_year?: string | number;
  preferred_subjects?: string[];
}

export interface NotificationPreferencesUpdateData {
  weekly_progress_reports?: boolean;
  new_feature_announcements?: boolean;
}

export interface PaymentHistoryItem {
  payment_id: string;
  plan_name: string;
  amount: number;
  status: string;
  payment_date: string;
  razorpay_payment_id: string;
}

interface ProfileState {
  profile: UserProfile | null;
  paymentHistory: PaymentHistoryItem[];
  isFetching: boolean;
  isFetchingHistory: boolean;
  isUpdating: boolean;
  isError: boolean;
  errorMessage: string;
  isSuccess: boolean;
  successMessage: string;
}

const initialState: ProfileState = {
  profile: null,
  isFetching: false,
  isUpdating: false,
  isError: false,
  errorMessage: "",
  isSuccess: false,
  successMessage: "",
  paymentHistory: [],
  isFetchingHistory: false,
};

// Fetch user profile
export const fetchProfile = createAsyncThunk(
  "profile/fetchProfile",
  async (_, thunkAPI) => {
    try {
      const response = await axios.get("/profile");

      if (response.status === 200) {
        return response.data;
      } else {
        return thunkAPI.rejectWithValue(response.data);
      }
    } catch (err: any) {
      if (!err.response) {
        console.error("Error:", err);
        throw err;
      }
      return thunkAPI.rejectWithValue(
        err.response.data || { message: "Failed to fetch profile" }
      );
    }
  }
);

// Update user profile (for EditProfileDialog - full_name, target_exam_year, avatar, preferred_subjects)
export const updateProfile = createAsyncThunk(
  "profile/updateProfile",
  async (data: ProfileUpdateData, thunkAPI) => {
    try {
      const response = await axios.patch("/profile", data);

      if (response.status === 200 || response.status === 201) {
        return response.data;
      } else {
        return thunkAPI.rejectWithValue(response.data);
      }
    } catch (err: any) {
      if (!err.response) {
        console.error("Error:", err);
        throw err;
      }
      return thunkAPI.rejectWithValue(
        err.response.data || { message: "Failed to update profile" }
      );
    }
  }
);

// Update notification preferences (for Profile.tsx - weekly_progress_reports, new_feature_announcements)
export const updateNotificationPreferences = createAsyncThunk(
  "profile/updateNotificationPreferences",
  async (data: NotificationPreferencesUpdateData, thunkAPI) => {
    try {
      const response = await axios.patch("/profile", data);

      if (response.status === 200 || response.status === 201) {
        return response.data;
      } else {
        return thunkAPI.rejectWithValue(response.data);
      }
    } catch (err: any) {
      if (!err.response) {
        console.error("Error:", err);
        throw err;
      }
      return thunkAPI.rejectWithValue(
        err.response.data || { message: "Failed to update notification preferences" }
      );
    }
  }
);

// Fetch Payment History
export const fetchPaymentHistory = createAsyncThunk(
  "profile/fetchPaymentHistory",
  async (_, thunkAPI) => {
    try {
      const response = await axios.get("/api/one-time-payment/history?limit=50&offset=0");
      if (response.status === 200) {
        return response.data;
      } else {
        return thunkAPI.rejectWithValue(response.data);
      }
    } catch (err: any) {
      return thunkAPI.rejectWithValue(
        err.response?.data || { message: "Failed to fetch payment history" }
      );
    }
  }
);

export const profileSlice = createSlice({
  name: "profile",
  initialState,
  reducers: {
    clearProfile: (state) => {
      state.profile = null;
      state.isError = false;
      state.errorMessage = "";
      state.isSuccess = false;
    },
    clearError: (state) => {
      state.isError = false;
      state.errorMessage = "";
    },
    clearSuccess: (state) => {
      state.isSuccess = false;
      state.successMessage = "";
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Profile
      .addCase(fetchProfile.pending, (state) => {
        state.isFetching = true;
        state.isError = false;
        state.errorMessage = "";
      })
      .addCase(fetchProfile.fulfilled, (state, action) => {
        state.isFetching = false;
        // API returns { profile: {...}, token: "..." }
        state.profile = action.payload?.profile || action.payload || null;
        state.isError = false;
      })
      .addCase(fetchProfile.rejected, (state, action: any) => {
        state.isFetching = false;
        state.isError = true;
        state.errorMessage =
          action.payload?.message || "Failed to fetch profile";
      })
      // Update Profile
      .addCase(updateProfile.pending, (state) => {
        state.isUpdating = true;
        state.isError = false;
        state.errorMessage = "";
        state.isSuccess = false;
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.isUpdating = false;
        // API returns { profile: {...}, token: "..." }
        state.profile = action.payload?.profile || action.payload || state.profile;
        state.isSuccess = true;
        state.successMessage = action.payload?.message || "Profile updated successfully";
        state.isError = false;
      })
      .addCase(updateProfile.rejected, (state, action: any) => {
        state.isUpdating = false;
        state.isError = true;
        state.errorMessage =
          action.payload?.message || "Failed to update profile";
        state.isSuccess = false;
      })
      // Update Notification Preferences
      .addCase(updateNotificationPreferences.pending, (state) => {
        state.isUpdating = true;
        state.isError = false;
        state.errorMessage = "";
        state.isSuccess = false;
      })
      .addCase(updateNotificationPreferences.fulfilled, (state, action) => {
        state.isUpdating = false;
        // API returns { profile: {...}, token: "..." }
        state.profile = action.payload?.profile || action.payload || state.profile;
        state.isSuccess = true;
        state.successMessage = action.payload?.message || "Preferences updated successfully";
        state.isError = false;
      })
      .addCase(updateNotificationPreferences.rejected, (state, action: any) => {
        state.isUpdating = false;
        state.isError = true;
        state.errorMessage =
          action.payload?.message || "Failed to update notification preferences";
        state.isSuccess = false;
      })

      // Fetch Payment History
      .addCase(fetchPaymentHistory.pending, (state) => {
        state.isFetchingHistory = true;
      })
      .addCase(fetchPaymentHistory.fulfilled, (state, action) => {
        state.isFetchingHistory = false;
        state.paymentHistory = action.payload.payments || [];
      })
      .addCase(fetchPaymentHistory.rejected, (state) => {
        state.isFetchingHistory = false;
        // We can optionally set an error state specific to history if needed, 
        // but for now we'll just stop the loading spinner.
      });
  },
});

export const { clearProfile, clearError, clearSuccess } = profileSlice.actions;

export const profileSelector = (state: any) => state.profile;

export const profileReducer = profileSlice.reducer;
export default profileSlice.reducer;

